import React, { useEffect, useState } from "react";
import { useWM } from "./store";
import { APPS } from "./apps";
import { useAuth } from "./AuthContext";
import { mailUnread } from "./mailUnread";

/**
 * HEXA App Rail — vertical auto-hide launcher pinned to the RIGHT edge.
 * Colorful per-app tint. Slides in on hover, otherwise peeks 6 px on the edge.
 */

// Colorful tint per app id — matches each sub-app's identity.
const APP_TINTS = {
  "admin-ai":  "#f59e0b",
  dashboard:   "#8b5cf6",
  wingman:     "#22d3ee",
  teamchat:    "#f43f5e",
  attendance:  "#10b981",
  projects:    "#f97316",
  crm:         "#06b6d4",
  quotation:   "#eab308",
  proposal:    "#0f2c5c",
  roles:       "#8b5cf6",
  dayrecap:    "#a855f7",
  chat:        "#6366f1",
  browser:     "#14b8a6",
  mail:        "#3b82f6",
  social:      "#ec4899",
  approvals:   "#84cc16",
  meetings:    "#0ea5e9",
  security:    "#ef4444",
  calculator:  "#f472b6",
  recorder:    "#a3e635",
  shortcuts:   "#94a3b8",
  notes:       "#fbbf24",
  imagegen:    "#d946ef",
  files:       "#f97316",
  calendar:    "#0ea5e9",
  word:        "#2563eb",
  excel:       "#16a34a",
  ppt:         "#e11d48",
  pdf:         "#dc2626",
  audio:       "#f59e0b",
  video:       "#a855f7",
};

const tintFor = (id) => APP_TINTS[id] || "#c4b5fd";

export default function Dock() {
  const { open, windows, focus } = useWM();
  const { user, appAccess } = useAuth();
  const [hovered, setHovered] = useState(false);
  const [mailUnreadTotal, setMailUnreadTotal] = useState(0);

  const apps = APPS.filter(
    (a) => !a.hideFromDock && !(a.requiresSuperAdmin && !user?.is_super_admin) && !appAccess?.hidden?.includes(a.id)
  );

  useEffect(() => {
    const unsub = mailUnread.subscribe((c) => setMailUnreadTotal(c.total || 0));
    return () => unsub();
  }, []);

  if (windows.length === 0) return null;

  return (
    <>
      {/* Invisible trigger strip on the far right edge */}
      <div
        data-testid="os-dock-trigger"
        onMouseEnter={() => setHovered(true)}
        className="fixed right-0 top-0 bottom-0 z-[99]"
        style={{ width: 14 }}
        aria-hidden
      />
      <div
        data-testid="os-dock"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-[100] flex flex-col items-center gap-1.5"
        style={{
          padding: "10px 8px",
          background: "rgba(12, 18, 36, 0.82)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: "1px solid rgba(139,92,246,0.22)",
          borderRight: "none",
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          boxShadow: "-8px 0 32px rgba(0,0,0,0.55)",
          maxHeight: "86vh",
          overflowY: "auto",
          transform: hovered
            ? "translate(0, -50%)"
            : "translate(calc(100% - 6px), -50%)",
          opacity: hovered ? 1 : 0.85,
          transition: "transform 320ms cubic-bezier(0.16, 1, 0.3, 1), opacity 220ms ease",
        }}
      >
        <div
          className="text-[8px] mono tracking-[0.3em] text-[#8b5cf6]/70 mb-1"
          style={{ writingMode: "vertical-rl" }}
        >
          APPS · {apps.length}
        </div>
        {apps.map((app) => {
          const openWin = windows.find((w) => w.appId === app.id);
          const isActive = !!openWin;
          const Icon = app.icon;
          const showBadge = app.id === "mail" && mailUnreadTotal > 0;
          const tint = tintFor(app.id);
          return (
            <button
              key={app.id}
              data-testid={`dock-app-${app.id}`}
              onClick={() => {
                if (openWin) focus(openWin.id);
                else open(app);
              }}
              title={app.title}
              className="relative grid place-items-center rounded-xl transition-all"
              style={{
                width: 40,
                height: 40,
                background: isActive
                  ? `linear-gradient(135deg, ${tint}33, ${tint}18)`
                  : "transparent",
                border: isActive ? `1px solid ${tint}55` : "1px solid transparent",
                boxShadow: isActive ? `0 0 12px ${tint}44` : "none",
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = `${tint}18`;
                  e.currentTarget.style.transform = "translateX(-3px)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.transform = "translateX(0)";
                }
              }}
            >
              <Icon size={20} strokeWidth={1.8} color={tint} />
              {isActive && (
                <span
                  aria-hidden
                  className="absolute"
                  style={{
                    right: -6,
                    top: "50%",
                    marginTop: -6,
                    width: 3,
                    height: 12,
                    borderRadius: 2,
                    background: tint,
                    boxShadow: `0 0 8px ${tint}`,
                  }}
                />
              )}
              {showBadge && (
                <span
                  data-testid="dock-mail-badge"
                  className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center mono text-[9px] font-bold leading-none"
                  style={{
                    background: "#ff7a2e",
                    color: "#0e1a22",
                    boxShadow: "0 0 10px rgba(255,122,46,0.65)",
                    border: "1.5px solid #0e1a22",
                  }}
                >
                  {mailUnreadTotal > 99 ? "99+" : mailUnreadTotal}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}
